import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Instagram } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About Milly", to: "/about" },
  { label: "Private Lessons", to: "/lessons" },
  { label: "Intensive", to: "/intensive" },
  {
    label: "Parents",
    children: [
      { label: "Parent Information", to: "/parent-info" },
      { label: "Policies", to: "/policies" },
      { label: "Underwater Videos", to: "/underwater-videos" },
      { label: "FAQ", to: "/faq" },
    ],
  },
  { label: "Gallery", to: "/gallery" },
  { label: "Merch", to: "/merch" },
  { label: "Swim Books", to: "/swim-books" },
  { label: "Reviews", to: "/testimonials" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const linkStyle = ({ isActive }) => ({
    color: isActive ? "var(--gold)" : "var(--navy)",
  });

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        backgroundColor: scrolled ? "rgba(249,248,246,0.82)" : "transparent",
        backdropFilter: scrolled ? "blur(18px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(212,175,55,0.2)" : "1px solid transparent",
      }}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/logo.png" alt="Mimi & Me Swimming" className="h-12 w-12 object-contain" />
          <span className="hidden sm:block leading-tight">
            <span className="font-display text-xl block" style={{ color: "var(--navy)" }}>Mimi &amp; Me</span>
            <span className="overline" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>Swimming</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 font-body text-sm">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setDropOpen(true)}
                onMouseLeave={() => setDropOpen(false)}
              >
                <button className="flex items-center gap-1" style={{ color: "var(--navy)" }} data-testid="nav-parents">
                  {item.label} <ChevronDown size={14} />
                </button>
                <AnimatePresence>
                  {dropOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                      className="absolute top-full left-0 pt-4 w-56"
                    >
                      <div className="bg-white rounded-xl p-2 shadow-[0_8px_32px_rgba(11,27,61,0.12)] border" style={{ borderColor: "rgba(212,175,55,0.2)" }}>
                        {item.children.map((c) => (
                          <NavLink key={c.to} to={c.to} className="block px-4 py-3 rounded-lg text-sm hover:bg-[var(--sand)] transition-colors" style={{ color: "var(--navy)" }}>
                            {c.label}
                          </NavLink>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink key={item.to} to={item.to} style={linkStyle} className="hover:opacity-70 transition-opacity" data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                {item.label}
              </NavLink>
            )
          )}
          <a href="https://instagram.com/mila_pilar444" target="_blank" rel="noreferrer" className="transition-colors hover:opacity-70" style={{ color: "var(--navy)" }} aria-label="Instagram" data-testid="nav-instagram"><Instagram size={20} /></a>
          <Link to="/contact" className="btn-primary !px-6 !py-3" data-testid="nav-cta">Request Availability</Link>
        </nav>

        <button className="lg:hidden p-2" onClick={() => setOpen(true)} style={{ color: "var(--navy)" }} data-testid="nav-mobile-open">
          <Menu size={26} />
        </button>
      </div>

      {createPortal(<AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] lg:hidden overflow-y-auto" style={{ backgroundColor: "var(--cream)" }}
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-20">
              <img src="/logo.png" alt="logo" className="h-12 w-12 object-contain" />
              <button onClick={() => setOpen(false)} style={{ color: "var(--navy)" }} data-testid="nav-mobile-close"><X size={28} /></button>
            </div>
            <div className="px-8 pt-2 pb-12 flex flex-col gap-1">
              {[{ label: "Home", to: "/" }, { label: "About Milly", to: "/about" }, { label: "Private Lessons", to: "/lessons" }, { label: "Swim Breakthrough Intensive", to: "/intensive" }, { label: "Parent Information", to: "/parent-info" }, { label: "Policies", to: "/policies" }, { label: "Underwater Videos", to: "/underwater-videos" }, { label: "FAQ", to: "/faq" }, { label: "Gallery", to: "/gallery" }, { label: "Merch", to: "/merch" }, { label: "Swim Books", to: "/swim-books" }, { label: "Reviews", to: "/testimonials" }, { label: "Contact", to: "/contact" }].map((l) => (
                <NavLink key={l.to} to={l.to} className="font-display text-2xl py-2" style={{ color: "var(--navy)" }} data-testid={`nav-mobile-${l.to.slice(1) || "home"}`}>{l.label}</NavLink>
              ))}
              <Link to="/contact" className="btn-primary mt-6 w-full" data-testid="nav-mobile-cta">Request Availability</Link>
              <a href="tel:5615776346" className="flex items-center gap-2 mt-4 justify-center" style={{ color: "var(--muted)" }} data-testid="nav-mobile-phone"><Phone size={16} /> 561-577-6346</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>, document.body)}
    </header>
  );
};
